import { match, P } from "ts-pattern";
import type { LngKeys as KnownKeys } from "components/materials/I18n/I18nStore";
import I18n from "components/materials/I18n";
import { I18nTextResource } from "common/resources/I18nText/I18nTextResource";
import { ISO639 } from "common/types/I18nText";

type AtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
	{
		[K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
	}[Keys];

export type I18nText = AtLeastOne<{
	[K in KnownKeys]?: string;
}>;

/**
 * Get the first defined translation.
 */
export function getDefaultTranslation(i18nText: I18nText): string {
	return match(i18nText)
		.with({ fr: P.string }, ({ fr }) => fr)
		.with({ en: P.string }, ({ en }) => en)
		.exhaustive();
}

/**
 * Translate the given text to the current language,
 * or return the default translation if the current language is not available.
 */
export function translate(i18nText: I18nText): string {
	return match(i18nText[I18n.getLang()])
		.with(P.nonNullable, (translation) => translation)
		.otherwise(() => getDefaultTranslation(i18nText));
}

/**
 * Translate the given text to the current language,
 * or return the first available translation if the current language is not available.
 */
export function translateISO369(i18nText: I18nTextResource[], preferedLang?: ISO639): string | null {
	const getTranslation = (search?: ISO639) => (search ? i18nText.find(({ lang }) => lang === search)?.text : null);
	const getFirstAvailableTranslation = () => i18nText[0]?.text ?? null;

	return getTranslation(I18n.getLang()) ?? getTranslation(preferedLang) ?? getFirstAvailableTranslation();
}
